import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  effect,
  ChangeDetectorRef,
  Renderer2,
  ElementRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonDirective } from '@shared/directives/button.directive';
import {
  closeButtonAnimation,
  dropdownMenuAnimation,
  offCanvasBackdropAnimation,
  offCanvasMenuAnimation,
} from './animations';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { CURRENT_USER, TOKEN } from 'app/app.constants';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { AuthService } from '@core/services/auth.service';
import { LoginResponse, LoginResponseRole2 } from '@core/models/client/auth';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { ExpiredSessionService } from '@core/services/expired-session.service';
import { Select, Store } from '@ngxs/store';
import { AuthSelectors } from 'app/auth/store/auth.selectors';
import { Observable, Subscription, find } from 'rxjs';
import { AuthActions } from 'app/auth/store/auth.actions';
import { ErrorDialogService } from '@core/services/error-dialog.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { CountBadgeComponent } from '@shared/ui/status/count-badge/count-badge.component';
import { NotificationBellComponent } from '@shared/ui/notification-bell/notification-bell.component';
import { NotificationFacade } from '@core/facades/notification.facade';
import { PushNotificationService } from '@core/services/push-notification.service';
import { AdminInboxService } from '@core/services/admin-inbox.service';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { IAccountRMMetadata } from 'app/auth/table-store/table.state.model';

@Component({
  selector: 'app-dashboard-layout',
  standalone: true,
  imports: [
    CommonModule,
    ButtonDirective,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    ConfirmDialogComponent,
    PermissionDirective,
    CountBadgeComponent,
    NotificationBellComponent
  ],
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
  animations: [
    closeButtonAnimation,
    offCanvasBackdropAnimation,
    offCanvasMenuAnimation,
    dropdownMenuAnimation,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardLayoutComponent implements OnInit {
  @ViewChild('toggleButton') toggleButton!: ElementRef;
  @ViewChild('menu') menu!: ElementRef;
  confirmDialogService = inject(ConfirmDialogService)
  

  @ViewChild('confirmDialog', { read: ViewContainerRef, static: true })
  confirmDialog!: ViewContainerRef;

  mainBreadCrumb!: string;
  nextBreadCrumb!: string;
  primaryBreadCrumb!: string;
  secondaryBreadCrumb!: string;
  currentUser!: LoginResponse | null;
  // showDialog: boolean = false;
  tokenExpiredUrl!: string;
  userName: string | null = null;
  userRoles!: LoginResponseRole2[];
  accountMetadata$!: Observable<IAccountRMMetadata>;
  subs: Subscription[] = []

  activatedRoute = inject(ActivatedRoute);
  route = inject(Router);
  breadCrumbService = inject(BreadCrumbService);
  authService = inject(AuthService);
  cdr = inject(ChangeDetectorRef);
  expiredSessionService = inject(ExpiredSessionService);
  private store = inject(Store);
  private errorDialogService = inject(ErrorDialogService);
  renderer = inject(Renderer2);
  private push = inject(PushNotificationService);
  private adminInbox = inject(AdminInboxService);
  private notify = inject(NotificationFacade);

  showNotificationPrompt = false;

  buttonState: 'visible' | 'hidden' = 'visible';
  menuState: 'visible' | 'hidden' = 'visible';
  navigationMenuState: 'visible' | 'hidden' = 'hidden';
  userMenuState: 'visible' | 'hidden' = 'hidden';
  accountMenuState: 'visible' | 'hidden' = 'hidden';
  productMenuState: 'visible' | 'hidden' = 'hidden';
  transactionsMenuState: 'visible' | 'hidden' = 'hidden';
  notificationsMenuState: 'visible' | 'hidden' = 'hidden';
  ordersMenuState: 'visible' | 'hidden' = 'hidden';
  settingsMenuState: 'visible' | 'hidden' = 'hidden';

  showDialog$!: Observable<boolean>;
  private dialogSubscription!: Subscription;

  constructor() {
    this.accountMetadata$ = this.store.select(TableSelectors.getAccountsRMMetadata());
    // this will watch for any changes to the navigation service and update the
    // bread crumb accordingly.
    effect(() => {
      this.mainBreadCrumb = this.breadCrumbService.currentPage()
        ?.main as string;
      this.nextBreadCrumb = this.breadCrumbService.currentPage()
        ?.next as string;
      this.primaryBreadCrumb = this.breadCrumbService.currentPage()
        ?.primary as string;
      this.secondaryBreadCrumb = this.breadCrumbService.currentPage()
        ?.secondary as string;

      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    this.getAccountRMMetadata();
    this.setDefaultAnimationFlags();
    this.userName = this.authService.getUserName();
    this.userRoles = this.authService.getUserRoles() ?? [];

    this.showDialog$ = this.errorDialogService.showDialog$;
    this.confirmDialogService.setViewContainerRef(this.confirmDialog);

    this.initPushNotifications();
    this.checkNotificationPrompt();
  }

  private checkNotificationPrompt(): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    if (sessionStorage.getItem('mango_notif_prompt_dismissed')) return;
    this.showNotificationPrompt = true;
    this.cdr.detectChanges();
  }

  async enableNotifications(): Promise<void> {
    this.showNotificationPrompt = false;
    await this.push.requestPermissionAndRegister();
    if (Notification.permission === 'granted') {
      window.location.reload();
      return;
    }
    this.cdr.detectChanges();
  }

  dismissNotificationPrompt(): void {
    this.showNotificationPrompt = false;
    sessionStorage.setItem('mango_notif_prompt_dismissed', '1');
    this.cdr.detectChanges();
  }

  private initPushNotifications(): void {
    this.push.refreshTokenIfGranted();
    this.adminInbox.refresh();

    this.push.onForegroundMessage((payload) => {
      const n = payload?.notification || {};
      const d = (payload?.data || {}) as Record<string, string>;
      this.adminInbox.refresh();
      this.notify.info(
        n.body || d['body'] || d['message'] || '',
        n.title || d['title'] || 'Notification'
      );
      this.cdr.detectChanges();
    });
  }

  setDefaultAnimationFlags() {
    this.buttonState = 'hidden';
    this.menuState = 'hidden';
    this.userMenuState = 'hidden';
    this.accountMenuState = 'hidden';
    this.transactionsMenuState = 'hidden';
    this.settingsMenuState = 'hidden';
  }

  toggleSidebarMenu() {
    this.buttonState = this.buttonState === 'hidden' ? 'visible' : 'hidden';
    this.menuState = this.menuState === 'hidden' ? 'visible' : 'hidden';
  }

  logout() {
    this.push.unregisterLocally();
    this.adminInbox.reset();
    this.authService.logout();
    this.route.navigateByUrl('/');
  }

  login() {
    // this.authService.logout();
    this.errorDialogService.closeDialog();
    this.route.navigateByUrl(`/auth/login`);
  }

  toggleNavigationMenu() {
    this.navigationMenuState =
      this.navigationMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleUsersDropdown(): void {
    this.userMenuState = this.userMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleAccountsDropdown(): void {
    this.accountMenuState =
      this.accountMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleProductsDropdown(): void {
    this.productMenuState =
      this.productMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleTransactionsDropdown(): void {
    this.transactionsMenuState =
      this.transactionsMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleNotificationsDropdown(): void {
    this.notificationsMenuState =
      this.notificationsMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleOrdersDropdown(): void {
    this.ordersMenuState =
      this.ordersMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  toggleSettingsDropdown(): void {
    this.settingsMenuState =
      this.settingsMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  async popOverOpen(event: any) {
    if (event.fromState === 'hidden' && event.toState === 'visible') {
      // we check if the dropdown is visible now
      (await event.fromState) === 'hidden' && event.toState === 'visible';
      this.renderer.listen('window', 'click', (e: Event) => {
        /**
         * Only run when toggleButton is not clicked
         * If we don't check this, all clicks (even on the toggle button) gets into this
         * section which in the result we might never see the menu open!
         * And the menu itself is checked here, and it's where we check just outside of
         * the menu and button the condition abbove must close the menu
         */
        if (
          !this.toggleButton.nativeElement.contains(e.target) &&
          !this.menu.nativeElement.contains(e.target)
        ) {
          this.navigationMenuState = 'hidden';
          this.cdr.detectChanges();
        }
      });
    }
  }

    getAccountRMMetadata() {
      // if the account metadata is not in store, dispatch an action to get it
      const sub = this.accountMetadata$.subscribe((metadata) => {
        if (!metadata) {
          this.store.dispatch(new TableActions.GetAccountsRMMetadata());
        }
      });
      this.subs.push(sub);
    }

    ngOnDestroy() {
      this.subs.forEach((sub) => sub.unsubscribe())
    }
  
}
